<template>
  <div ref="crudTableFitRoot" :data-testid="getTestId('site-data-table')">
    <v-data-table
      :headers="headers"
      :items="sites"
      class="elevation-1"
      hide-default-footer
      hide-details="auto"
      :loading="apiLoading"
      :loading-text="$t('fileDetail.loadingData')"
      :custom-sort="customSort"
      :options.sync="tableOptions"
      @update:options="changedOptions"
    >
      <template slot="top">
        <v-toolbar flat class="solvit-crud-table-toolbar">
          <v-toolbar-title style="align-items: center; display: flex">
            <v-icon left> mdi-solar-panel </v-icon>{{ $t('site.infoTitle') }}
          </v-toolbar-title>
          <v-divider class="mx-4" inset vertical />
          <v-spacer />
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            :label="$t('site.dataSearchLabel')"
            single-line
            hide-details="auto"
            :data-testid="getTestId('site-search-input')"
          />
          <v-spacer />
          <v-btn
            outlined
            style="font-weight: 400"
            class="mb-2 solvit-crud-outline-btn solvit-crud-outline-btn--primary"
            :data-testid="getTestId('site-add-button')"
            @click="dialog = !dialog"
          >
            <v-icon size="medium"> mdi-plus </v-icon>
            {{ $t('common.addNew') }}
          </v-btn>
        </v-toolbar>
        <v-dialog
            :key="`site-dialog-${dialog}-${editedIndex}`"
            v-model="dialog"
            :max-width="modalLayout.XXL"
            v-bind="modalP2FormViewport"
            @keydown.esc="close"
            :data-testid="getTestId('site-dialog')"
          >
            <v-form ref="siteForm" v-model="valid" @submit.prevent="save">
              <v-card class="site-form-dialog-card">
                <CrudFormDialogHeader
                  :kicker="$t('site.infoTitle')"
                  :title="formTitle"
                  show-close
                  @close="close"
                />
                <v-card-text class="site-form-dialog-body pt-5 text-body-2">
                  <v-row>
                    <v-col cols="6">
                      <v-card flat class="ml-5">
                        <v-row align="baseline">
                          <v-col cols="4" class="custom-padding required">
                            <label>{{ $t('project.projects') }}</label>
                          </v-col>
                          <v-col cols="8" class="custom-padding">
                            <v-select
                              v-model="editedItem.project"
                              :items="projects"
                              item-text="name"
                              item-value="id"
                              return-object
                              hide-details="auto"
                              dense
                              outlined
                              :rules="[requiredSelectRule]"
                              :data-testid="getTestId('site-project-select')"
                            />
                          </v-col>
                        </v-row>
                        <v-row align="baseline">
                          <v-col cols="4" class="custom-padding required">
                            <label>{{ $t('site.label') }}</label>
                          </v-col>
                          <v-col cols="8" class="custom-padding">
                            <v-text-field
                              v-model="editedItem.name"
                              hide-details="auto"
                              dense
                              outlined
                              :rules="[requiredRule]"
                              :data-testid="getTestId('site-name-input')"
                            />
                          </v-col>
                        </v-row>
                        <v-row align="baseline">
                          <v-col cols="4" class="custom-padding">
                            <label>{{ $t('project.completionDate') }}</label>
                          </v-col>
                          <v-col cols="8" class="custom-padding">
                            <v-menu
                              v-model="datePickerMenu"
                              :close-on-content-click="false"
                              offset-y
                              min-width="290px"
                              z-index="250"
                              content-class="site-date-picker-menu"
                            >
                              <template slot="activator" slot-scope="{ on, attrs }">
                                <v-text-field
                                  v-model="editedItem.establishedDate"
                                  readonly
                                  v-bind="attrs"
                                  hide-details="auto"
                                  append-icon="mdi-calendar"
                                  :clearable="false"
                                  dense
                                  outlined
                                  class="site-date-field"
                                  :data-testid="getTestId('site-installation-date-input')"
                                  v-on="on"
                                  @click:append.stop="datePickerMenu = !datePickerMenu"
                                />
                              </template>
                              <v-date-picker
                                v-model="editedItem.establishedDate"
                                no-title
                                scrollable
                                color="primary"
                                @input="datePickerMenu = false"
                              />
                            </v-menu>
                          </v-col>
                        </v-row>
                        <v-row align="baseline">
                          <v-col cols="4" class="custom-padding">
                            <label>{{ $t('site.orthoImage') }}</label>
                          </v-col>
                          <v-col cols="8" class="custom-padding">
                            <div>
                              <a
                                v-if="!editedItem?.siteImageFile?.name"
                                :href="editedItem?.siteImageUrl"
                                >{{ editedItem?.siteImageUrl?.split('/').pop() }}</a
                              >
                            </div>
                            <div
                              v-if="editedItem?.siteImageFile?.name"
                              style="text-overflow: ellipsis; width: 280px; margin-top: 10px"
                            >
                              {{ editedItem?.siteImageFile?.name }}
                            </div>
                            <input
                              type="file"
                              style="display: none"
                              accept="image/tiff,.tif"
                              @change="e => handleFileChange(e)"
                            />

                            <v-btn
                              outlined
                              class="solvit-crud-outline-btn solvit-crud-outline-btn--primary"
                              style="display: inline-block; margin-top: 5px"
                              @click="triggerInputFile"
                            >
                              <v-icon left> mdi-upload </v-icon>{{ $t('common.upload') }}
                            </v-btn>
                          </v-col>
                        </v-row>
                        <v-row align="baseline">
                          <v-col cols="4" class="custom-padding">
                            <label>{{ $t('site.powerLabel') }}</label>
                          </v-col>
                          <v-col cols="8" class="custom-padding">
                            <v-text-field
                              v-model="editedItem.powerCapacity"
                              hide-details="auto"
                              :rules="[numericRule]"
                              suffix="kW"
                              dense
                              outlined
                              :data-testid="getTestId('site-capacity-input')"
                            />
                          </v-col>
                        </v-row>
                        <v-row align="baseline" class="site-pv-option site-pv-option--legacy">
                          <v-col cols="4" class="custom-padding">
                            <label>{{ $t('facility.pvModuleModel') }}</label>
                            <div class="site-pv-option__hint d-flex align-center mt-1">
                              <v-icon x-small class="mr-1" color="secondary">
                                mdi-alert-circle-outline
                              </v-icon>
                              <span class="site-pv-option__hint-text">{{
                                $t('site.legacyPvTitle')
                              }}</span>
                            </div>
                          </v-col>
                          <v-col cols="8" class="custom-padding">
                            <v-text-field
                              v-model="editedItem.pvModuleMaker"
                              hide-details="auto"
                              dense
                              outlined
                              :placeholder="$t('site.legacyPlaceholder')"
                            />
                          </v-col>
                        </v-row>
                        <v-row align="baseline" class="site-pv-option site-pv-option--recommended">
                          <v-col cols="4" class="custom-padding">
                            <label>{{ $t('facility.pvModuleModel') }}</label>
                            <div class="site-pv-option__hint d-flex align-center mt-1">
                              <v-icon x-small class="mr-1" color="primary">mdi-star-outline</v-icon>
                              <span class="site-pv-option__hint-text site-pv-option__hint-text--accent">{{
                                $t('site.recommendedPvTitle')
                              }}</span>
                            </div>
                          </v-col>
                          <v-col cols="8" class="custom-padding">
                            <v-combobox
                              v-model="editedItem.panelProduct"
                              :items="panelProducts"
                              item-text="text"
                              item-value="value"
                              hide-details="auto"
                              dense
                              outlined
                              :placeholder="$t('site.productPlaceholder')"
                              :data-testid="getTestId('site-panel-product-select')"
                            />
                          </v-col>
                        </v-row>

                        <v-row align="baseline">
                          <v-col cols="4" class="custom-padding required">
                            <label>{{ $t('facility.pvModuleCount') }}</label>
                          </v-col>
                          <v-col cols="8" class="custom-padding">
                            <v-text-field
                              v-model="editedItem.pvModuleCount"
                              hide-details="auto"
                              dense
                              outlined
                              :suffix="$t('common.count')"
                              :rules="[requiredRule, numericRule]"
                            />
                          </v-col>
                        </v-row>
                        <v-row align="baseline">
                          <v-col cols="4" class="custom-padding required">
                            <label>{{ $t('facility.pvModuleOutput') }}</label>
                          </v-col>
                          <v-col cols="8" class="custom-padding">
                            <v-text-field
                              v-model="editedItem.pvModuleOutput"
                              hide-details="auto"
                              suffix="W"
                              :rules="[requiredRule, numericRule]"
                              dense
                              outlined
                            />
                          </v-col>
                        </v-row>

                        <v-row align="baseline">
                          <v-col cols="4" class="custom-padding">
                            <label>{{ $t('facility.plantOwner') }}</label>
                          </v-col>
                          <v-col cols="8" class="custom-padding">
                            <v-select
                              v-model="editedItem.ownerCompany"
                              :items="companies"
                              item-text="companyName"
                              item-value="id"
                              return-object
                              hide-details="auto"
                              dense
                              outlined
                              @change="onCompanyChange(editedItem.ownerCompany, 'ownerCompany')"
                            />
                          </v-col>
                        </v-row>
                        <v-row align="baseline">
                          <v-col cols="4" class="custom-padding required">
                            <label>{{ $t('facility.plantOperator') }}</label>
                          </v-col>
                          <v-col cols="8" class="custom-padding">
                            <v-select
                              v-model="editedItem.operationCompany"
                              :items="companies"
                              item-text="companyName"
                              item-value="id"
                              return-object
                              :rules="[requiredSelectRule]"
                              hide-details="auto"
                              dense
                              outlined
                              @change="
                                onCompanyChange(editedItem.operationCompany, 'operationCompany')
                              "
                            />
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col cols="12" class="custom-padding pt-1">
                            <v-alert
                              type="info"
                              dense
                              text
                              colored-border
                              border="left"
                              class="mb-0"
                            >
                              <div class="text-body-2 font-weight-medium">
                                {{ $t('facility.companyMapVisibilityTitle') }}
                              </div>
                              <div class="text-caption mt-1">
                                {{ $t('facility.companyMapVisibilityHint') }}
                              </div>
                            </v-alert>
                          </v-col>
                        </v-row>
                        <v-row align="baseline" rows="12">
                          <v-col cols="4" class="custom-padding">
                            <label>{{ $t('common.description') }}</label>
                          </v-col>
                          <v-col cols="8" class="custom-padding">
                            <v-textarea
                              v-model="editedItem.description"
                              outlined
                              dense
                              hide-details="auto"
                              :data-testid="getTestId('site-description-input')"
                            />
                          </v-col>
                        </v-row>

                        <!-- 🆕 담당자 선택 섹션 (선택사항) -->
                        <v-row align="baseline">
                          <v-col cols="4" class="custom-padding">
                            <label
                              >{{ $t('site.table.managers') }}
                              <span class="site-manager-optional">{{
                                $t('site.managerOptional')
                              }}</span></label
                            >
                          </v-col>
                          <v-col cols="8" class="custom-padding">
                            <!-- 상황별 메시지 표시 -->
                            <v-alert
                              v-if="managerStatusMessage.type !== 'success'"
                              dense
                              text
                              :type="managerStatusMessage.type"
                              class="site-manager-status mb-2"
                            >
                              <div class="text-body-2">{{ managerStatusMessage.message }}</div>
                              <div class="text-caption text--secondary mt-1">
                                {{ managerStatusMessage.action }}
                              </div>
                            </v-alert>

                            <!-- 담당자가 있는 경우에만 combobox 표시 -->
                            <v-combobox
                              v-if="companyManagerList.length > 0"
                              :value="getSelectedManagersForCombobox"
                              :items="companyManagerList"
                              item-text="displayName"
                              item-value="id"
                              multiple
                              chips
                              deletable-chips
                              :placeholder="$t('site.managerSelectPlaceholder')"
                              dense
                              outlined
                              hide-details="auto"
                              :loading="companyManagerListLoading"
                              :disabled="companyManagerListLoading"
                              return-object
                              clearable
                              :data-testid="getTestId('site-managers-select')"
                              @change="onManagersChange"
                            >
                              <template slot="selection" slot-scope="{ item, index }">
                                <v-chip v-if="index < 3" small outlined color="primary">
                                  {{ item.displayName }}
                                </v-chip>
                                <span v-else-if="index === 3" class="secondary--text text-caption ml-2">
                                  (+{{ editedItem.selectedManagers.length - 3 }} more)
                                </span>
                              </template>
                            </v-combobox>
                          </v-col>
                        </v-row>
                      </v-card>
                    </v-col>
                    <v-col>
                      <v-card class="mr-5" flat>
                        <v-row align="baseline" style="margin-bottom: 10px">
                          <v-col cols="4" class="custom-padding">
                            <label>{{ $t('common.address') }}</label>
                          </v-col>
                          <v-col cols="8" class="custom-padding">
                            <div v-if="editedItem.address" style="margin-bottom: 8px;">
                              <div style="display: flex; align-items: center; justify-content: space-between;">
                                <div style="flex: 1;">
                                  <strong>{{ $t('site.currentAddress') }}</strong> {{ editedItem.address }}
                                </div>
                                <v-btn
                                  small
                                  outlined
                                  class="solvit-crud-outline-btn solvit-crud-outline-btn--primary"
                                  @click="openSiteAddressDialog"
                                  style="margin-left: 8px;"
                                >
                                  <v-icon small>mdi-pencil</v-icon>
                                  {{ $t('site.change') }}
                                </v-btn>
                              </div>
                            </div>
                            <v-btn
                              v-if="!editedItem.address"
                              outlined
                              class="solvit-crud-outline-btn solvit-crud-outline-btn--primary"
                              style="margin: 8px 0 0 0"
                              :data-testid="getTestId('site-address-search-button')"
                              @click="openSiteAddressDialog"
                            >
                              <v-icon>mdi-magnify</v-icon>
                              {{ $t('site.searchAddressButton') }}
                            </v-btn>
                          </v-col>
                        </v-row>
                      </v-card>
                      <OpenLayerMapBox
                        :location="siteLocation"
                        :rotation="editedItem.rotation"
                        :data-testid="getTestId('site-map')"
                        @get-rotation="getRotation"
                      />
                      <v-card flat>
                        <div
                          style="
                            margin-top: 10px;
                            font-size: 14px;
                            color: var(--v-text-base);
                            font-weight: bold;
                            margin-bottom: 8px;
                          "
                        >
                          {{ $t('site.mapRotation') }}
                          <span style="display: inline-flex; align-items: center; margin-left: 8px">
                            <span
                              style="
                                background: rgba(127, 127, 127, 0.2);
                                padding: 2px 6px;
                                border-radius: 3px;
                                margin: 0 2px;
                                font-size: 11px;
                              "
                              >Alt</span
                            >
                            <v-icon small color="text" style="margin: 0 2px">mdi-plus</v-icon>
                            <span
                              style="
                                background: rgba(127, 127, 127, 0.2);
                                padding: 2px 6px;
                                border-radius: 3px;
                                margin: 0 2px;
                                font-size: 11px;
                              "
                              >Shift</span
                            >
                            <v-icon small color="text" style="margin: 0 2px">mdi-plus</v-icon>
                            <v-icon small color="text" style="margin: 0 2px">mdi-mouse</v-icon>
                            <span
                              style="
                                background: rgba(127, 127, 127, 0.2);
                                padding: 2px 6px;
                                border-radius: 3px;
                                margin: 0 2px;
                                font-size: 11px;
                              "
                              >drag</span
                            >
                          </span>
                        </div>
                        <div
                          style="
                            font-size: 12px;
                            color: var(--v-secondary-base);
                            background: rgba(127, 127, 127, 0.1);
                            padding: 8px;
                            border-radius: 4px;
                            margin-bottom: 8px;
                          "
                        >
                          {{ $t('site.currentRotation') }}
                          <span style="color: var(--v-text-base); font-weight: bold"
                            >{{ Number(editedItem.rotation || 0).toFixed(1) }}°</span
                          >
                        </div>
                        <div
                          style="
                            font-size: 12px;
                            color: var(--v-secondary-base);
                            background: rgba(127, 127, 127, 0.1);
                            padding: 8px;
                            border-radius: 4px;
                          "
                        >
                          {{ $t('site.currentCoords') }}
                          <span style="color: var(--v-text-base); font-weight: bold">{{
                            $t('site.coordsInline', {
                              lon: editedItem.location?.coordinates?.[0]?.toFixed(6) || '0.000000',
                              lat: editedItem.location?.coordinates?.[1]?.toFixed(6) || '0.000000'
                            })
                          }}</span>
                        </div>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-card-text>
                <v-card-actions class="site-form-dialog-actions px-4 pb-3 pt-2">
                  <v-spacer />
                  <v-btn outlined class="solvit-crud-outline-btn" @click="close">{{ $t('common.cancel') }}</v-btn>
                  <v-btn
                    outlined
                    class="solvit-crud-outline-btn solvit-crud-outline-btn--primary ml-2"
                    type="submit"
                    :data-testid="getTestId('site-save-button')"
                  >
                    {{ $t('common.save') }}
                  </v-btn>
                </v-card-actions>
              </v-card>
              <SiteAddressDialog
                :site-address-dialog="siteAddressDialog"
                :callback-fun="getAddress"
                @update:site-address-dialog="handleSiteAddressDialogUpdate"
                @input="handleSiteAddressDialogUpdate"
              />
            </v-form>
        </v-dialog>
      </template>
      <template slot="item.powerCapacity" slot-scope="{ item }">
        <span>{{ item?.powerCapacity?.toFixed(1) }}</span>
      </template>
      <template slot="item.location.coordinates" slot-scope="{ item }">
        <span v-if="item?.location?.coordinates">
          {{
            $t('site.coordsInline', {
              lon: item.location.coordinates[0]?.toFixed(6) || '0.000000',
              lat: item.location.coordinates[1]?.toFixed(6) || '0.000000'
            })
          }}
        </span>
        <span v-else>-</span>
      </template>

      <!-- 🆕 담당자 정보 표시 -->
      <template slot="item.managers" slot-scope="{ item }">
        <div v-if="item.managers && item.managers.length > 0">
          <v-chip
            v-for="manager in item.managers.slice(0, 2)"
            :key="manager.seq"
            small
            outlined
            color="primary"
            class="ma-1"
          >
            {{ createManagerDisplayName(manager) }}
          </v-chip>
          <span v-if="item.managers.length > 2" class="text-caption secondary--text">
            (+{{ item.managers.length - 2 }})
          </span>
        </div>
        <span v-else class="secondary--text">-</span>
      </template>
      <template slot="item.actions" slot-scope="{ item }">
        <FixDeleteBtn :key="item.id" :item="item" @editItem="editItem" @deleteItem="deleteItem" />
      </template>
      <template slot="no-data">
        <v-btn outlined class="solvit-crud-outline-btn" @click="initialize">{{ $t('fileDetail.refresh') }}</v-btn>
      </template>
    </v-data-table>
    <div class="text-center pt-2">
      <v-pagination
        v-model="page"
        :length="pageCount"
        :total-visible="10"
        next-icon="mdi-chevron-right"
        prev-icon="mdi-chevron-left"
      />
    </div>
  </div>
</template>

<script>
  import { ref, reactive, computed, watch, onMounted, onUnmounted } from 'vue';
  import Api from '../../apis/Api';
  import formDataApi from '@/apis/formDataApi';
  import FixDeleteBtn from '../button/FixDeleteBtn';
      import SiteAddressDialog from '../dialog/SitesAddressDialog.vue';
  import { getSortParam } from '../../utils/sortUtils';
  import { getTestId } from '@/utils/testUtils';
  import { debounce } from '@/utils/simpleDebounce';
  import { createLatestRequestGuard } from '@/utils/latestRequestWins';
  import { SiteFormFactory } from '../../models/SiteForm';
  import { SiteDataTransformService } from '../../services/SiteDataTransformService';
  import { 
    createDefaultTableOptions, 
    buildSortParam, 
    buildPaginationParams, 
    buildApiUrl, 
    transformApiResponse,
    calcItemsPerPageToFit,
    resolveCrudTableFitMetrics,
    CRUD_TABLE_FIT_DEFAULTS
  } from '../../utils/tableUtils';
  import i18n from '@/plugins/i18n';
  import { MODAL_MAX_WIDTH, MODAL_P2_FORM_VIEWPORT } from '@/constants/modalLayout';

  export default {
    components: {
      OpenLayerMapBox: () => import('../map/ol/OpenLayerMapBox'),
      FixDeleteBtn,
      SiteAddressDialog,
      CrudFormDialogHeader: () => import('@/components/common/CrudFormDialogHeader.vue')
    },

    computed: {
      headers() {
        void this.$i18n.locale;
        return [
          { text: this.$t('site.table.siteName'), value: 'name' },
          { text: this.$t('site.table.projectName'), value: 'project.name', sortable: false },
          { text: this.$t('site.table.establishedDate'), value: 'establishedDate' },
          { text: this.$t('site.table.powerCapacityKw'), value: 'powerCapacity' },
          { text: this.$t('site.table.pvModuleMaker'), value: 'pvModuleMaker' },
          { text: this.$t('site.table.pvModuleCount'), value: 'pvModuleCount' },
          { text: this.$t('site.table.pvModuleOutput'), value: 'pvModuleOutput' },
          { text: this.$t('site.table.locationCoords'), value: 'location.coordinates' },
          { text: this.$t('site.table.owner'), value: 'ownerCompany.companyName', sortable: false },
          { text: this.$t('site.table.operator'), value: 'operationCompany.companyName', sortable: false },
          { text: this.$t('site.table.managers'), value: 'managers', sortable: false },
          { text: this.$t('site.table.actions'), value: 'actions', sortable: false }
        ];
      }
    },

    setup() {
      const CONSTANTS = {
        DEFAULT_ITEMS_PER_PAGE: 10,
        DEFAULT_LOCATION: [127.37957, 36.372795],
        API_DOMAINS: {
          SITES: 'sites',
          PROJECTS: 'projects'
        },
        SORT_FIELD_MAPPING: {
          name: ['name'],
          'project.name': ['project.name'],
          establishedDate: ['establishedDate'],
          powerCapacity: ['powerCapacity'],
          pvModuleCount: ['pvModuleCount'],
          pvModuleOutput: ['pvModuleOutput'],
          'location.coordinates': ['location.coordinates'],
          description: ['description']
        }
      };

      const valid = ref(true);
      const siteForm = ref(null);
      const requiredRule = v => !!v || i18n.t('common.required');
      const requiredSelectRule = v => v.id > 0 || i18n.t('common.requiredSelect');
      const numericRule = v => {
        return !isNaN((v)) || i18n.t('common.numericOnly');
      };

      // tableUtils를 사용한 기본 테이블 옵션 생성
      const tableOptions = ref(createDefaultTableOptions(['id'], [true], CONSTANTS.DEFAULT_ITEMS_PER_PAGE));
      const siteAddressDialog = ref(false);
      const menu = ref(false);
      const datePickerMenu = ref(false);

      const itemsPerPage = ref(CONSTANTS.DEFAULT_ITEMS_PER_PAGE);
      const crudTableFitRoot = ref(null);
      const siteFitReady = ref(false);
      const siteInitialLoadSettled = ref(false);
      let pendingSiteOptions = null;
      let siteFitProgrammaticOptionsChange = false;
      let siteFitIgnoreNextResize = false;
      // 첫 API 요청 전에 fallback 기준으로 page size 적용 (mounted 후 DOM 실측으로 보정)
      {
        const fitted = calcItemsPerPageToFit(
          resolveCrudTableFitMetrics(null, {
            rowHeightFallback: CRUD_TABLE_FIT_DEFAULTS.rowHeight,
            chromeHeightFallback: CRUD_TABLE_FIT_DEFAULTS.chromeHeight
          })
        );
        itemsPerPage.value = fitted;
        tableOptions.value.itemsPerPage = fitted;
      }
      const pageCount = ref(0);
      const paging = ref(true);
      const page = ref(1);

      const domain = CONSTANTS.API_DOMAINS.SITES;
      const dialog = ref(false);
      const search = ref('');
      const siteLocation = ref(CONSTANTS.DEFAULT_LOCATION);

      const sites = ref([]);
      const projects = ref([]);
      const panelProducts = ref([]);
      const companies = ref([]);
      const editedIndex = ref(-1);

      const editedItem = reactive(SiteFormFactory.createEmpty());

      const apiLoading = ref(true);
      const siteRequestGuard = createLatestRequestGuard();

      const defaultItem = reactive(SiteFormFactory.createEmpty());

      const currentLocation = ref([]);
      const companyManagerList = ref([]);
      const companyManagerListLoading = ref(false);
      const allManagersList = ref([]);

      const formTitle = computed(() => {
        return editedIndex.value === -1 ? i18n.t('common.addNew') : i18n.t('common.edit');
      });

      /**
       * tableUtils를 사용한 정렬 파라미터 변환
       * @param {Array} sortBy - 정렬할 필드들
       * @returns {Array} API 요청용 정렬 필드 배열
       */
      const changeSortByArrayToApiParam = sortBy => {
        // tableUtils의 buildSortParam을 사용하여 정렬 파라미터 생성
        if (sortBy && sortBy.length > 0) {
          const sortableFields = {
            'name': 'name',
            'project.name': 'project.name',
            'establishedDate': 'establishedDate',
            'powerCapacity': 'powerCapacity',
            'pvModuleCount': 'pvModuleCount',
            'pvModuleOutput': 'pvModuleOutput',
            'location.coordinates': 'location.coordinates',
            'ownerCompany.companyName': 'ownerCompany.companyName',
            'operationCompany.companyName': 'operationCompany.companyName',
            'description': 'description',
            'id': 'id'
          };
          
          // buildSortParam은 전체 정렬 파라미터를 반환하므로 필드만 추출
          const sortParam = buildSortParam(sortBy, tableOptions.value.sortDesc, sortableFields);
          const [field] = sortParam.split(',');
          return [field];
        }
        return ['id'];
      };

      const customSort = (items, _index, _isDesc) => {
        return items;
      };

      const changedOptions = val => {
        if (siteFitProgrammaticOptionsChange) {
          return;
        }
        if (!siteFitReady.value) {
          pendingSiteOptions = val;
          return;
        }
        
        // val에서 정렬 정보 추출하여 직접 사용
        const sortBy = val.sortBy || tableOptions.value.sortBy;
        const sortDesc = val.sortDesc || tableOptions.value.sortDesc;
        
        console.log("searchSiteData 호출 파라미터:", {
          search: search.value,
          page: page.value,
          sortBy: sortBy,
          sortDesc: sortDesc
        });
        
        searchSiteData(
          search.value, 
          page.value, 
          sortBy, 
          sortDesc
        );
      };

      /**
       * tableUtils를 사용한 site 데이터 조회
       * @param {string} keyword - 검색 키워드
       * @param {number} pageNum - 페이지 번호
       * @param {Array} sortByParam - 정렬 필드
       * @param {Array} sortDescParam - 정렬 방향
       */
      /**
       * tableUtils를 사용한 site 데이터 조회
       * @param {string} keyword - 검색 키워드
       * @param {number} pageNum - 페이지 번호
       * @param {Array} sortByParam - 정렬 필드
       * @param {Array} sortDescParam - 정렬 방향
       */
      const searchSiteData = async (keyword, pageNum, sortByParam, sortDescParam) => {
        const request = siteRequestGuard.begin();
        apiLoading.value = true;
        
        // tableUtils를 사용하여 정렬 파라미터 생성
        const sortableFields = {
          'name': 'name',
          'project.name': 'project.name',
          'establishedDate': 'establishedDate',
          'powerCapacity': 'powerCapacity',
          'pvModuleCount': 'pvModuleCount',
          'pvModuleOutput': 'pvModuleOutput',
          'location.coordinates': 'location.coordinates',
          'description': 'description',
          'id': 'id'
        };
        
        // 정렬 파라미터 생성 - ProjectCrudTable과 동일한 방식
        let sortParam = '';
        if (sortByParam && sortByParam.length > 0) {
          const sortFields = sortByParam.map((field, index) => {
            const direction = (sortDescParam && sortDescParam[index]) ? 'desc' : 'asc';
            return `${field},${direction}`;
          });
          sortParam = sortFields.join(',');
        } else {
          // 기본값: id,desc
          sortParam = 'id,desc';
        }
        

        // 페이지 번호는 1부터 시작하도록 조정
        const currentPage = pageNum || page.value;
        const adjustedPage = currentPage - 1; // API는 0부터 시작
        


        


        try {
          // 사이트 데이터 조회 - 가독성 개선: API 호출 바로 위에서 params 구성
          const res = await Api.getApiResource(`/${domain}`, {
            size: itemsPerPage.value,
            page: adjustedPage,
            sort: sortParam,
            ...(keyword && keyword.trim() && { keyword: keyword.trim() })
          }, { signal: request.signal });
          
          if (!siteRequestGuard.isCurrent(request.token)) return;

          
          // API 응답 구조 확인 후 직접 처리
          if (res.data?.content?.content && Array.isArray(res.data.content.content)) {
            // 기존 API 응답 구조 사용
            const items = res.data.content.content.map(item =>
              SiteFormFactory.fromApiResponse(item)
            );
            
            sites.value = items;
            pageCount.value = res.data.content.page?.totalPages || 0;
            // 페이지 변경 시에만 현재 페이지 유지, 초기 로드 시에만 1로 설정
            if (pageNum && pageNum > 1) {
              page.value = pageNum;
            } else {
              page.value = (res.data.content.page?.number || 0) + 1;
            }
            

          } else {
            // tableUtils를 사용하여 응답 데이터 변환 (대안)
            try {
              const transformedData = transformApiResponse(res, (items) => {

                const transformed = items.map(item =>
                  SiteFormFactory.fromApiResponse(item)
                );

                return transformed;
              });
              

              
              // 변환된 데이터로 상태 업데이트
              sites.value = transformedData.items;
              pageCount.value = transformedData.pageCount;
              page.value = transformedData.page;
              
            } catch (error) {
              console.error("tableUtils 변환 에러:", error);
              // 에러 발생 시 빈 배열로 설정
              sites.value = [];
              pageCount.value = 0;
              page.value = 1;
            }
          }

          // 프로젝트 데이터는 별도로 조회하지 않음 (중복 방지)
        } catch (error) {
          if (!siteRequestGuard.isCurrent(request.token)) return;
          console.error('사이트 데이터 조회 실패:', error);
          // 에러 발생 시 빈 배열로 설정
          sites.value = [];
          pageCount.value = 0;
          page.value = 1;
        } finally {
          if (siteRequestGuard.isCurrent(request.token)) {
            apiLoading.value = false;
            siteRequestGuard.finish(request.token);
          }
        }
      };

      const initialize = () => {
        searchSiteData();
      };

      let siteFitResizeObserver = null;
      let siteFitCalibrating = false;
      let siteFitCalibrationToken = 0;

      const applySitePageSizeFit = (refetch = false) => {
        if (siteFitCalibrating && refetch) {
          return itemsPerPage.value;
        }
        const root = crudTableFitRoot.value?.$el || crudTableFitRoot.value || null;
        const metrics = resolveCrudTableFitMetrics(root, {
          rowHeightFallback: CRUD_TABLE_FIT_DEFAULTS.rowHeight,
          chromeHeightFallback: CRUD_TABLE_FIT_DEFAULTS.chromeHeight
        });
        const next = calcItemsPerPageToFit(metrics);
        if (next === itemsPerPage.value) {
          return next;
        }
        if (refetch) {
          siteFitProgrammaticOptionsChange = true;
        }
        itemsPerPage.value = next;
        tableOptions.value = {
          ...tableOptions.value,
          itemsPerPage: next,
          ...(refetch ? { page: 1 } : {})
        };
        if (refetch) {
          siteFitCalibrating = true;
          const calibrationToken = ++siteFitCalibrationToken;
          page.value = 1;
          Promise.resolve(
            searchSiteData(search.value, 1, tableOptions.value.sortBy, tableOptions.value.sortDesc)
          ).finally(() => {
            if (calibrationToken === siteFitCalibrationToken) {
              siteFitCalibrating = false;
            }
          });
        }
        return next;
      };

      const onSitePageSizeFitResize = debounce(
        () => {
          if (siteInitialLoadSettled.value) {
            applySitePageSizeFit(true);
          }
        },
        CRUD_TABLE_FIT_DEFAULTS.resizeDebounceMs
      );
      const scheduleSitePageSizeFitRefetch = () => onSitePageSizeFitResize();

      // 프로젝트 목록, 회사 목록, 사이트 데이터를 한 번만 조회
      const loadInitialData = async () => {
        try {
          // 프로젝트 목록 조회
          const projectRes = await Api.read('/projects/lists');

          projects.value = projectRes?.data?.content;
          
          // 회사 목록 조회
          const companiesRes = await Api.read('/companies/list');
          companies.value = companiesRes?.data?.content || [];

          
          // 사이트 데이터 조회 (초기 로드) - changeOption이 자동으로 실행 -중복실행방지
          // await searchSiteData('', 1, ['id'], [true]);
        } catch (error) {
          console.error('초기 데이터 로딩 오류:', error);
        }
      };

      const bindSiteFitResizeObserver = () => {
        if (siteFitResizeObserver || typeof ResizeObserver === 'undefined') {
          return;
        }
        const host = document.querySelector('.admin-crud-fit-host');
        if (!host) {
          return;
        }
        siteFitIgnoreNextResize = true;
        siteFitResizeObserver = new ResizeObserver(() => {
          if (siteFitIgnoreNextResize) {
            siteFitIgnoreNextResize = false;
            return;
          }
          onSitePageSizeFitResize();
        });
        siteFitResizeObserver.observe(host);
      };

      // mounted에서 초기 정렬 옵션 설정 및 데이터 로딩
      onMounted(async () => {
        // 초기 정렬 옵션을 명시적으로 설정
        tableOptions.value.sortBy = ['id'];
        tableOptions.value.sortDesc = [true];
        
        // 초기 데이터 로딩 (프로젝트, 회사 목록 + 사이트 데이터)
        await loadInitialData();
        siteFitProgrammaticOptionsChange = true;
        applySitePageSizeFit(false);
        siteFitReady.value = true;
        const initialOptions = pendingSiteOptions;
        pendingSiteOptions = null;
        searchSiteData(
          search.value,
          page.value,
          initialOptions?.sortBy || tableOptions.value.sortBy,
          initialOptions?.sortDesc || tableOptions.value.sortDesc
        );
        
        // 이벤트 버스를 통한 지도 이벤트 리스너 등록
        window.eventBus.$on('ol-event', handleOlEvent);
        window.addEventListener('resize', onSitePageSizeFitResize);
        if (window.visualViewport) {
          window.visualViewport.addEventListener('resize', onSitePageSizeFitResize);
        }

      });

      // 컴포넌트 언마운트 시 이벤트 리스너 제거
      onUnmounted(() => {
        siteFitCalibrationToken += 1;
        siteRequestGuard.cancel();
        window.eventBus.$off('ol-event', handleOlEvent);
        onSitePageSizeFitResize.cancel?.();
        window.removeEventListener('resize', onSitePageSizeFitResize);
        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', onSitePageSizeFitResize);
        }
        if (siteFitResizeObserver) {
          siteFitResizeObserver.disconnect();
          siteFitResizeObserver = null;
        }
      });

      watch(apiLoading, (val, oldVal) => {
        if (oldVal === true && val === false) {
          if (!siteInitialLoadSettled.value) {
            onSitePageSizeFitResize.cancel?.();
            siteInitialLoadSettled.value = true;
            siteFitProgrammaticOptionsChange = false;
            bindSiteFitResizeObserver();
            return;
          }
          siteFitProgrammaticOptionsChange = false;
          scheduleSitePageSizeFitRefetch();
        }
      });

              

      const editItem = async item => {
        console.log('전달받은 item:', item);
        console.log('item 타입:', typeof item);
        console.log('item 키들:', Object.keys(item));
        console.log('item.id:', item?.id);
        console.log('item.name:', item?.name);
        
        if (!item) {
          console.error('item이 undefined입니다!');
          return;
        }
        
        apiLoading.value = true;
        siteLocation.value = item.location?.coordinates || CONSTANTS.DEFAULT_LOCATION;
        editedIndex.value = sites.value.indexOf(item);

        try {

          
          Object.assign(editedItem, SiteFormFactory.fromApiResponse(item));


          if (item.panelProductSeq && panelProducts.value) {
            const selectedPanelProduct = panelProducts.value.find(
              product => product.value === item.panelProductSeq
            );
            if (selectedPanelProduct) {
              editedItem.panelProduct = selectedPanelProduct;

            }
          }

          apiLoading.value = false;
          dialog.value = true;
        } catch (error) {
          console.error('=== editItem 에러 발생 ===');
          console.error('에러 내용:', error);
          console.error('에러 스택:', error.stack);
          apiLoading.value = false;
        }
      };

      const deleteItem = async item => {
        console.log('전달받은 item:', item);
        console.log('item 타입:', typeof item);
        console.log('item 키들:', Object.keys(item));
        console.log('item.id:', item?.id);
        console.log('item.name:', item?.name);
        
        if (!item) {
          console.error('item이 undefined입니다!');
          return;
        }
        
        apiLoading.value = true;
        const index = sites.value.indexOf(item);

        
        try {
          if (confirm(i18n.t('common.confirmDeleteSelected'))) {

            await Api.delete(`/${domain}/${item.id}`, item);
            sites.value.splice(index, 1);
            initialize();
          } else {
            // 삭제 취소됨
          }
        } catch (error) {
          console.error('=== deleteItem 에러 발생 ===');
          console.error('에러 내용:', error);
          console.error('에러 스택:', error.stack);
          alert(i18n.t('site.deleteApiError'));
        } finally {
          apiLoading.value = false;
        }
      };

      const close = () => {
        dialog.value = false;
        initDialog();
      };

      const initDialog = () => {
        Object.assign(editedItem, { ...defaultItem });
        editedIndex.value = -1;
        datePickerMenu.value = false;
      };

      const disableThingsWhenSave = disable => {
        document.querySelectorAll('button').forEach(item => {
          item.disabled = !!disable;
          item.style._hover = 'none';
        });

        const html = document.querySelector('html');
        if (disable) {
          html.style.pointerEvents = 'none';
        } else {
          html.style.pointerEvents = 'auto';
        }
      };

      const validateForm = () => {
        return valid.value;
      };

      const createFormData = data => {
        return SiteDataTransformService.toFormDataForUpload(data);
      };

      const save = async () => {
        if (!siteForm.value?.validate()) return;
        if (!validateForm()) {
          return;
        }
        

        const formData = createFormData(editedItem);

        if (editedIndex.value > -1) {
          disableThingsWhenSave(true);
          try {
            const res = await formDataApi.put(`/${domain}/${editedItem.id}`, formData);
            if (res.data.result == 'F') {
              throw new Error(i18n.t('common.unknownError'));
            }
            Object.assign(sites.value[editedIndex.value], res.data);

            initialize();
          } catch (error) {
            console.error('수정 실패:', error);
          } finally {
            disableThingsWhenSave(false);
            close();
          }
        } else {
          disableThingsWhenSave(true);
          try {
            const res = await formDataApi.post(`/${domain}`, formData);
            if (res.data.result == 'F') {
              throw new Error(i18n.t('common.unknownError'));
            }
            sites.value.push(res.data);
            initialize();
          } catch (error) {
            console.error('생성 실패:', error);
          } finally {
            disableThingsWhenSave(false);
            close();
          }
        }
      };

      const thumbNailImage = imageUrl => {
        return URL.createObjectURL(imageUrl);
      };

      const goLocation = value => {

        const coords = value.trim().split(',');
        if (coords && coords.length == 2) {
          if (isNaN(coords[0].trim()) || isNaN(coords[1].trim())) {
            return;
          } else {
            coords[0] = coords[0].trim();
            coords[1] = coords[1].trim();
            if (
              coords[0].match(/\d{2,3}\.\d{1,}/) !== null &&
              coords[1].match(/\d{2,3}\.\d{1,}/) !== null
            ) {
              siteLocation.value = coords;
              editedItem.location = {
                coordinates: coords,
                crs: { properties: { name: 'ESPG:4326' }, type: 'name' },
                type: 'Point'
              };
            }
          }
        }
      };

      const getAddress = ({ latitude: lat, longitude: lon, addressName }) => {

        editedItem.location = {
          coordinates: [lon, lat],
          crs: { properties: { name: 'ESPG:4326' }, type: 'name' },
          type: 'Point'
        };
        editedItem.address = addressName;
        siteLocation.value = [lon, lat];
      };

      const handleFileChange = e => {
        if (e.target.files[0].size > 1024 * 1024 * 1024) {
          alert('Only files smaller than 1GB can be uploaded.');
          return;
        } else {
          editedItem.siteImageFile = e.target.files[0];
        }

      };

      const openSiteAddressDialog = () => {
        console.log('주소 검색 다이얼로그 열기');
        siteAddressDialog.value = true;
      };

      const handleSiteAddressDialogUpdate = (value) => {
        console.log('주소 검색 다이얼로그 상태 업데이트:', value);
        siteAddressDialog.value = value;
        
        // Vue 2에서 강제 업데이트
        if (value === false) {
          console.log('다이얼로그 강제 닫기 시도');
          setTimeout(() => {
            siteAddressDialog.value = false;
          }, 0);
        }
      };


      const triggerInputFile = () => {
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) {
          fileInput.click();
        }
      };

      const getRotation = rotation => {
        const rotationInDegrees = (rotation * 180) / Math.PI;
        editedItem.rotation = Number(rotationInDegrees.toFixed(2));
      };

      /**
       * 이벤트 버스를 통한 OpenLayerMap 이벤트 처리
       * 마우스 클릭으로 좌표를 설정할 때 호출됨
       */
      const handleOlEvent = (eventData) => {
        console.log('이벤트 버스에서 받은 이벤트:', eventData);
        
        if (eventData.from === 'ol-map' && eventData.type === 'set-point') {
          // 마우스 클릭으로 설정된 좌표값을 받아서 업데이트
          const [longitude, latitude] = eventData.payload;
          
          // editedItem의 location 업데이트
          editedItem.location = {
            coordinates: [longitude, latitude],
            crs: { properties: { name: 'EPSG:4326' }, type: 'name' },
            type: 'Point'
          };
          
          // siteLocation 업데이트 (지도 표시용)
          siteLocation.value = [longitude, latitude];
          
          // 주소 정보 초기화 (마우스 클릭으로 위치를 변경했으므로)
          editedItem.address = '';
          
          // production에서도 좌표 확인을 위해 console.info 사용
          console.info('🗺️ 마우스 클릭으로 좌표 업데이트:', { longitude, latitude });
        }
      };

      const loadPanelProductList = async () => {

        try {
          const response = await Api.read('/panelproduct/panelProductList');

          panelProducts.value = response?.data?.content;
        } catch (error) {
          console.error('panelProducts 로드 실패:', error);
        }
      };

      const loadManagersList = async () => {

        companyManagerListLoading.value = true;

        try {
          const response = await Api.read('/company-managers/list');

          if (response?.data?.content && Array.isArray(response?.data?.content)) {
            let managers = response.data.content.map(manager => ({
              ...manager,
              displayName: createManagerDisplayName(manager)
            }));
            allManagersList.value = managers;
            filterManagersByCompany(managers);

          } else {
            console.warn('담당자 데이터가 배열이 아님:', response?.data);
            allManagersList.value = [];
            companyManagerList.value = [];
          }
        } catch (error) {
          console.error('담당자 목록 로드 실패:', error);
          allManagersList.value = [];
          companyManagerList.value = [];
        } finally {
          companyManagerListLoading.value = false;
        }
      };

      const createManagerDisplayName = manager => {
        const displayName = manager?.name;
        return displayName;
      };

      const onManagersChange = selectedManagers => {
        console.log('담당자 선택 변경:', selectedManagers);
        editedItem.selectedManagers = selectedManagers;
        console.log('변환된 selectedManagers:', editedItem.selectedManagers);
      };

      const hasSelectedManagers = computed(() => {
        editedItem.ownerCompany?.id;
        editedItem.operationCompany?.id;

        return (editedItem.selectedManagers?.length || 0) >= 1;
      });

      const managerStatusMessage = computed(() => {
        editedItem.ownerCompany?.id;
        editedItem.operationCompany?.id;

        const hasCompany = editedItem.ownerCompany?.id || editedItem.operationCompany?.id;
        const hasManagers = companyManagerList.value.length > 0;

        // 담당자 선택을 선택사항으로 변경
        if (!hasCompany) {
          return {
            type: 'info',
            message: i18n.t('site.managerHint.needCompany'),
            action: i18n.t('site.managerHint.optional')
          };
        }

        if (!hasManagers) {
          return {
            type: 'info',
            message: i18n.t('site.managerHint.noManagers'),
            action: i18n.t('site.managerHint.optional')
          };
        }

        return {
          type: 'success',
          message: i18n.t('site.managerHint.canSelect'),
          action: i18n.t('site.managerHint.ready')
        };
      });

      const updateSelectedManagers = () => {
        if (!editedItem.selectedManagers) {
          return;
        }

        editedItem.selectedManagers = editedItem.selectedManagers.map(manager => {
          const foundManager = companyManagerList.value.find(m => m.id === manager.id);
          return foundManager ? { ...manager, seq: foundManager.seq } : manager;
        });

        console.log('Updated selectedManagers:', editedItem.selectedManagers);
      };

      const getSelectedManagersForCombobox = computed(() => {
        return editedItem.selectedManagers.map(manager => ({
          id: manager.id,
          displayName: manager.displayName || createManagerDisplayName(manager),
          phone: manager.phone || ''
        }));
      });

      watch(
        () => [editedItem.ownerCompany, editedItem.operationCompany],
        ([newOwnerCompany, newOperationCompany], [oldOwnerCompany, oldOperationCompany]) => {
          
          const ownerChanged = newOwnerCompany?.id !== oldOwnerCompany?.id;
          const operationChanged = newOperationCompany?.id !== oldOperationCompany?.id;

          if (ownerChanged || operationChanged) {
            console.log('회사 정보 변경 감지:', {
              ownerCompany: { old: oldOwnerCompany?.id, new: newOwnerCompany?.id },
              operationCompany: { old: oldOperationCompany?.id, new: newOperationCompany?.id }
            });
            updateManagerState();
          }
        },
        { immediate: false }
      );

      const updateManagerState = () => {
        filterManagersByCompany(allManagersList.value);
        validateAndCleanSelectedManagers();
        notifyManagerStateChange();
      };

      const filterManagersByCompany = (managers) => {
        if (!managers || managers.length === 0) {
          console.log('전체 담당자 목록이 없습니다.');
          companyManagerList.value = [];
          return;
        }

        const ownerCompanyId = editedItem.ownerCompany?.id;
        const operationCompanyId = editedItem.operationCompany?.id;

        console.log('필터링할 회사 ID들:', { ownerCompanyId, operationCompanyId });

        const filteredManagers = [];

        if (ownerCompanyId) {
          const ownerManagers = managers.filter(
            manager => manager.companySeq === ownerCompanyId
          );
          filteredManagers.push(...ownerManagers);
          console.log('소유사 담당자:', ownerManagers);
        }

        if (operationCompanyId) {
          const operationManagers = managers.filter(
            manager =>
              manager.companySeq === operationCompanyId &&
              !filteredManagers.some(existing => existing.id === manager.id)
          );
          filteredManagers.push(...operationManagers);
          console.log('운영사 담당자:', operationManagers);
        }

        companyManagerList.value = filteredManagers;
        console.log('최종 필터링된 담당자 목록:', companyManagerList.value);
      };

      /**
       * 선택된 담당자 검증 및 정리
       */
      const validateAndCleanSelectedManagers = () => {
        if (!editedItem.selectedManagers || editedItem.selectedManagers.length === 0) {
          return;
        }

        const validManagers = editedItem.selectedManagers.filter(manager => {
          const ownerCompanyId = editedItem.ownerCompany?.id;
          const operationCompanyId = editedItem.operationCompany?.id;
          const isValid =
            manager.companySeq === ownerCompanyId || manager.companySeq === operationCompanyId;

          if (!isValid) {
            console.log(`담당자 ${manager.name} 제거됨 (회사 불일치)`);
          }
          return isValid;
        });

        if (validManagers.length !== editedItem.selectedManagers.length) {
          editedItem.selectedManagers = validManagers;
          console.log('회사 변경으로 인한 담당자 필터링 완료:', validManagers);
        }
      };

      /**
       * 담당자 상태 변경 알림
       */
      const notifyManagerStateChange = () => {
        const hasManagers = companyManagerList.value.length > 0;
        const selectedCount = editedItem.selectedManagers?.length || 0;

        console.log('담당자 상태 변경:', {
          availableManagers: companyManagerList.value.length,
          selectedManagers: selectedCount,
          hasManagers
        });

        if (!hasManagers) {
          console.log('필터링된 담당자가 없습니다. 회사를 확인해주세요.');
        }
      };

      const validateManagersBeforeSave = () => {
        const errors = [];

        // 담당자 입력을 선택사항으로 변경 - 주소 검색과 독립적으로 작동
        if (editedItem.selectedManagers && editedItem.selectedManagers.length > 0) {
          // 담당자가 선택된 경우에만 유효성 검사
          if (!editedItem.ownerCompany?.id && !editedItem.operationCompany?.id) {
            errors.push(i18n.t('site.managerHint.needCompany'));
          }

          const invalidManagers = editedItem.selectedManagers.filter(manager => {
            const ownerCompanyId = editedItem.ownerCompany?.id;
            const operationCompanyId = editedItem.operationCompany?.id;
            return (
              manager.companySeq !== ownerCompanyId && manager.companySeq !== operationCompanyId
            );
          });

          if (invalidManagers.length > 0) {
            errors.push(
              i18n.t('site.managerHint.invalidSelected', {
                names: invalidManagers.map(m => m.name).join(', ')
              })
            );
          }
        }

        return {
          isValid: errors.length === 0,
          errors
        };
      };

      const onCompanyChange = (company, propertyName) => {
        console.log(`${propertyName} 변경:`, company);
        editedItem[propertyName] = company;
      };

      const debouncedSearch = debounce(searchSiteData, 200);

      watch(dialog, async val => {
        if (val) {
          await loadPanelProductList();
          await loadManagersList();
        } else {
          await close();
        }
      });

      watch(search, val => {
        console.log('검색어:', val);
        debouncedSearch(val, 1, tableOptions.value.sortBy, tableOptions.value.sortDesc);
      });

      watch(page, val => {
        if (siteFitProgrammaticOptionsChange) {
          return;
        }
        console.log('페이지:', val);
        const currentSortBy = tableOptions.value.sortBy || ['id'];
        const currentSortDesc = tableOptions.value.sortDesc || [true];
        console.log('페이지 변경 시 정렬 정보:', { currentSortBy, currentSortDesc });
        searchSiteData(search.value, val, currentSortBy, currentSortDesc);
      });

                      return {
          valid,
          siteForm,
          requiredRule,
          requiredSelectRule,
          tableOptions,
          siteAddressDialog,
          menu,
          datePickerMenu,
          itemsPerPage,
          crudTableFitRoot,
          pageCount,
          paging,
          page,
          domain,
          dialog,
          modalLayout: MODAL_MAX_WIDTH,
          modalP2FormViewport: MODAL_P2_FORM_VIEWPORT,
          search,
          siteLocation,
          sites,
          projects,
          panelProducts,
          companies,
          editedIndex,
          editedItem,
          apiLoading,
          defaultItem,
          currentLocation,
          companyManagerList,
          companyManagerListLoading,
          allManagersList,

          formTitle,
          hasSelectedManagers,
          managerStatusMessage,
          getSelectedManagersForCombobox,
          updateSelectedManagers,

          getTestId,
          changeSortByArrayToApiParam,
          customSort,
          changedOptions,
          searchSiteData,
          initialize,
          loadInitialData,
          editItem,
          deleteItem,
          close,
          initDialog,
          disableThingsWhenSave,
          validateForm,
          createFormData,
          numericRule,
          save,
          thumbNailImage,
          goLocation,
          getAddress,
          handleFileChange,
          openSiteAddressDialog,
          handleSiteAddressDialogUpdate,
          triggerInputFile,
          getRotation,
          loadPanelProductList,
          loadManagersList,
          createManagerDisplayName,
          onManagersChange,

          filterManagersByCompany,
          updateManagerState,
          validateAndCleanSelectedManagers,
          notifyManagerStateChange,
          validateManagersBeforeSave,
          onCompanyChange,
          debouncedSearch,
          handleOlEvent
        };
    }
  };
</script>

<style scoped>
  .site-form-dialog-card {
    background-color: var(--v-surface-base) !important;
    border-color: var(--v-borderSubtle-base) !important;
  }

  .site-form-dialog-body .custom-padding > label {
    color: var(--v-secondary-base);
    font-weight: 500;
    font-size: 0.8125rem;
  }

  .site-form-dialog-actions {
    border-top: 1px solid var(--v-borderSubtle-base);
  }

  .site-manager-optional {
    color: var(--v-secondary-base);
    font-size: 0.75rem;
    font-weight: 400;
  }

  /* PV: 행 전체 채색 대신 왼쪽 액센트 + 옅은 틴트 */
  .site-pv-option {
    margin: 6px 0;
    border-radius: 6px;
    border: 1px solid var(--v-borderSubtle-base);
    background-color: color-mix(in srgb, var(--v-surface-base) 96%, var(--v-text-base));
  }

  .site-pv-option--legacy {
    border-left: 3px solid var(--v-secondary-base);
  }

  .site-pv-option--recommended {
    border-left: 3px solid var(--v-primary-base);
    background-color: color-mix(in srgb, var(--v-primary-base) 7%, var(--v-surface-base));
  }

  .site-pv-option__hint-text {
    font-size: 0.6875rem;
    line-height: 1.3;
    color: var(--v-secondary-base);
  }

  .site-pv-option__hint-text--accent {
    color: var(--v-primary-base);
  }

  .site-manager-status {
    margin-top: 0;
  }

  .site-manager-status ::v-deep .v-alert__wrapper {
    align-items: flex-start;
  }

  .custom-padding {
    padding: 8px 12px !important;
  }

  .required::after {
    content: '*';
    color: var(--v-error-base);
    margin-left: 4px;
  }
</style>

<style>
  /* v-menu content는 포탈이라 scoped 미적용 — 다이얼로그(z≈200) 위에 표시 */
  .site-date-picker-menu {
    z-index: 250 !important;
  }
</style>
